/**
 * The one active challenge Home surfaces, as a scoreboard row (specs/home-page AC-26, AC-28, AC-37):
 * you on the left with a status word, the score or the first-kickoff countdown in the middle with the
 * games left, the opponent on the right. Renders nothing when there is no active challenge (AC-29).
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { View, Text, Pressable, Platform, Animated, Easing } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useTheme } from '../../../src/theme/useTheme';
import { formatPointsDisplay } from '../../../src/friends/helpers/formatPoints.helper';
import { FriendAvatar } from '../../friends/shared/FriendAvatar';
import { normalizeDisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import { formatKickoffCountdown } from '../../../src/home/helpers/homeCountdown.helper';
import { HOME_COPY, HOME_TEST_IDS } from '../../../src/home/constants/home.constants';
import type { HomeActiveTileModel, HomeTileAvatar } from '../../../src/home/types/home.types';
import { HomeRegionSkeleton } from '../HomeRegionState/HomeRegionSkeleton';
import {
  ACTIVE_ROW_GRADIENT_END,
  ACTIVE_TILE_AVATAR_SIZE,
  getActiveChallengeTileStyles,
} from './ActiveChallengeTile.styles';

const LIVE_PULSE_MS = 1200;
const GRADIENT_START = { x: 0, y: 0.5 };
const GRADIENT_END = { x: 1, y: 0.5 };

export type ActiveStatus = 'leading' | 'behind' | 'level';

export interface ActiveChallengeTileProps {
  model: HomeActiveTileModel | null;
  isLoading: boolean;
  onPress: (challengeId: string) => void;
  nowMs?: number;
}

const avatarId = (avatar: HomeTileAvatar) =>
  avatar.avatarId ? normalizeDisplayAvatarId(avatar.avatarId) : null;

/** The word under "You" once a score exists; nothing before kickoff (AC-37). */
export function activeStatusFor(score: HomeActiveTileModel['score']): ActiveStatus | null {
  if (!score) return null;
  if (score.mine > score.theirs) return 'leading';
  if (score.mine < score.theirs) return 'behind';
  return 'level';
}

const STATUS_COPY: Record<ActiveStatus, string> = {
  leading: HOME_COPY.ACTIVE_LEADING,
  behind: HOME_COPY.ACTIVE_BEHIND,
  level: HOME_COPY.ACTIVE_LEVEL,
};
type StatusTextStyleKey = 'statusLeading' | 'statusBehind' | 'statusLevel';
type StatusDotStyleKey = 'liveDotLeading' | 'liveDotBehind' | 'liveDotLevel';
const STATUS_TEXT_STYLE: Record<ActiveStatus, StatusTextStyleKey> = {
  leading: 'statusLeading',
  behind: 'statusBehind',
  level: 'statusLevel',
};
const STATUS_DOT_STYLE: Record<ActiveStatus, StatusDotStyleKey> = {
  leading: 'liveDotLeading',
  behind: 'liveDotBehind',
  level: 'liveDotLevel',
};

function useLivePulse(active: boolean): Animated.Value {
  const opacity = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    if (!active) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, { toValue: 0.35, duration: LIVE_PULSE_MS, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
        Animated.timing(opacity, { toValue: 1, duration: LIVE_PULSE_MS, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => {
      loop.stop();
      opacity.setValue(1);
    };
  }, [active, opacity]);
  return opacity;
}

export const ActiveChallengeTile: React.FC<ActiveChallengeTileProps> = ({ model, isLoading, onPress, nowMs }) => {
  const theme = useTheme();
  const styles = useMemo(() => getActiveChallengeTileStyles(theme), [theme]);
  // Read the clock per render: the row re-renders when the list refetches, which keeps the countdown honest.
  const now = nowMs ?? Date.now();
  const challengeId = model?.challengeId;
  const status = activeStatusFor(model?.score ?? null);
  const pulse = useLivePulse(status != null);
  const handlePress = useCallback(() => {
    if (challengeId) onPress(challengeId);
  }, [onPress, challengeId]);

  if (isLoading) {
    return <HomeRegionSkeleton variant="tile" testID={`${HOME_TEST_IDS.tileSkeleton}-active`} />;
  }
  if (!model) return null;

  const opponentName = model.isGroup ? model.title : model.opponent?.name ?? model.title;
  const format = model.isGroup ? HOME_COPY.GROUP : HOME_COPY.ONE_V_ONE;

  return (
    <Pressable
      style={({ pressed, hovered }) => [
        styles.rowOuter,
        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        pressed && styles.rowPressed,
        hovered && Platform.OS === 'web' && styles.rowHover,
      ]}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={model.title}
      testID={HOME_TEST_IDS.activeTile}
    >
      <LinearGradient colors={[theme.background.card, ACTIVE_ROW_GRADIENT_END]} start={GRADIENT_START} end={GRADIENT_END} style={styles.row}>
        <View style={styles.side}>
          <FriendAvatar
            avatarId={avatarId(model.self)}
            avatarConfig={model.self.avatarConfig}
            fallbackUrl={model.self.avatarUrl ?? undefined}
            nameForInitials={model.self.name}
            size={ACTIVE_TILE_AVATAR_SIZE}
            style={styles.avatarSelf}
            small
          />
          <View style={styles.sideText}>
            <Text style={styles.name} numberOfLines={1}>{HOME_COPY.ACTIVE_YOU}</Text>
            {status && (
              <View style={styles.statusRow} testID={`${HOME_TEST_IDS.activeStatus}-${status}`}>
                <Animated.View style={[styles.liveDot, styles[STATUS_DOT_STYLE[status]], { opacity: pulse }]} />
                <Text style={[styles.statusText, styles[STATUS_TEXT_STYLE[status]]]}>{STATUS_COPY[status]}</Text>
              </View>
            )}
          </View>
        </View>

        <View style={styles.center}>
          {model.score ? (
            <View style={styles.score} testID={HOME_TEST_IDS.activeScore}>
              <Text style={styles.scoreMine}>{formatPointsDisplay(model.score.mine)}</Text>
              <Text style={styles.scoreSep}>–</Text>
              <Text style={styles.scoreTheirs}>{formatPointsDisplay(model.score.theirs)}</Text>
            </View>
          ) : model.kickoff ? (
            <Text style={styles.kickoff} testID={HOME_TEST_IDS.activeKickoff}>
              {formatKickoffCountdown(model.kickoff, now)}
            </Text>
          ) : null}
          <Text style={styles.gamesLeft} testID={HOME_TEST_IDS.activeGamesLeft}>{HOME_COPY.gamesLeft(model.matchesLeft)}</Text>
        </View>

        <View style={[styles.side, styles.sideRight]}>
          <View style={[styles.sideText, styles.sideTextRight]}>
            <Text style={styles.name} numberOfLines={1}>{opponentName}</Text>
            <Text style={styles.format}>{format}</Text>
          </View>
          {model.opponent && (
            <FriendAvatar
              avatarId={avatarId(model.opponent)}
              avatarConfig={model.opponent.avatarConfig}
              fallbackUrl={model.opponent.avatarUrl ?? undefined}
              nameForInitials={model.opponent.name}
              size={ACTIVE_TILE_AVATAR_SIZE}
              style={styles.avatarOpponent}
              small
            />
          )}
        </View>
      </LinearGradient>
    </Pressable>
  );
};
